import { useMemo } from "react";

import { useFeatureGatedFilter } from "hooks/useFeatureGatedFilter";

import { Dashboard, Workspace } from "models/workspace";

import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";

import useDashboards from "./useDashboards";

/**
 * Hook that returns dashboards (Personal + Organization + System) filtered to the target workspace.
 * Uses provided workspace or falls back to current workspace from context.
 */
export function useWorkspaceDashboards(workspace?: Workspace): Dashboard[] {
  const { data: allDashboardsData } = useDashboards();
  const { workspace: currentWorkspace } = useWorkspaceContext();
  const { filterDashboards } = useFeatureGatedFilter();

  // Use provided workspace or fall back to current workspace
  const targetWorkspace = workspace || currentWorkspace;

  return useMemo(() => {
    if (!targetWorkspace?.dashboards) return [];

    // Get all dashboard types (Personal + Organization + System) that are in this workspace
    const workspaceDashboardIds = targetWorkspace.dashboards.map((d) => d.dashboardId);
    const workspaceDashboards =
      allDashboardsData?.dashboards?.filter((dashboard) =>
        workspaceDashboardIds.includes(dashboard.dashboardId)
      ) || [];

    return filterDashboards(workspaceDashboards);
  }, [allDashboardsData?.dashboards, targetWorkspace?.dashboards, filterDashboards]);
}
